"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Eye, Calendar, User, FlaskConical, Hash, AlertTriangle, FileText } from "lucide-react"

interface ViewTestDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  controlId: string
  controlName: string
  riskId: string
  assignmentId: string
}

interface TestDetails {
  TestID: number
  TestDate: string
  TesterName: string
  TestingMethod: string
  SampleSize: number
  ExceptionsFound: number
  TestResult: string
  TestingNotes: string
  CreatedDate: string
}

export default function ViewTestDetailsModal({
  isOpen,
  onClose,
  controlId,
  controlName,
  riskId,
  assignmentId
}: ViewTestDetailsModalProps) {
  const [testDetails, setTestDetails] = useState<TestDetails | null>(null)
  const [loading, setLoading] = useState(false)

  // Fetch test details when modal opens
  useEffect(() => {
    if (isOpen && controlId && riskId && assignmentId) {
      fetchTestDetails()
    }
  }, [isOpen, controlId, riskId, assignmentId])

  const fetchTestDetails = async () => {
    try {
      setLoading(true)
      const response = await fetch(`/api/control-tests?assignmentId=${assignmentId}&controlId=${controlId}&riskId=${riskId}`)
      if (response.ok) {
        const tests = await response.json()
        if (tests.length > 0) {
          // Get the most recent test
          const latestTest = tests.sort((a: any, b: any) => 
            new Date(b.TestDate).getTime() - new Date(a.TestDate).getTime()
          )[0]
          setTestDetails(latestTest)
        }
      }
    } catch (error) {
      console.error('Error fetching test details:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTestResultBadgeColor = (result: string) => {
    switch (result) {
      case 'Passed': return 'bg-green-50 text-green-700 border-green-200'
      case 'Failed': return 'bg-red-50 text-red-700 border-red-200'
      case 'Passed with Exceptions': return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      case 'Not Applicable': return 'bg-gray-50 text-gray-700 border-gray-200'
      default: return 'bg-gray-50 text-gray-700 border-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-600" />
            Test Details
          </DialogTitle>
          <DialogDescription>
            Test information for: <span className="font-semibold">{controlName}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : testDetails ? (
          <div className="space-y-6 py-4">
            {/* Test Result Badge */}
            <div className="flex items-center justify-center">
              <Badge 
                variant="outline" 
                className={`text-lg px-4 py-2 ${getTestResultBadgeColor(testDetails.TestResult)}`}
              >
                {testDetails.TestResult}
              </Badge>
            </div>

            {/* Test Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Test Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Test Date
                </Label>
                <p className="text-sm font-medium">{formatDate(testDetails.TestDate)}</p>
              </div>

              {/* Tested By */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <User className="h-4 w-4" />
                  Tested By
                </Label>
                <p className="text-sm font-medium">{testDetails.TesterName}</p>
              </div>

              {/* Testing Method */}
              {testDetails.TestingMethod && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <FlaskConical className="h-4 w-4" />
                    Testing Method
                  </Label>
                  <p className="text-sm font-medium">{testDetails.TestingMethod}</p>
                </div>
              )}

              {/* Sample Size */}
              {testDetails.SampleSize && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Hash className="h-4 w-4" />
                    Sample Size
                  </Label>
                  <p className="text-sm font-medium">{testDetails.SampleSize.toLocaleString()}</p>
                </div>
              )}

              {/* Exceptions Found */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <AlertTriangle className="h-4 w-4" />
                  Exceptions Found
                </Label>
                <p className={`text-sm font-medium ${testDetails.ExceptionsFound > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {testDetails.ExceptionsFound}
                </p>
              </div>

              {/* Test Created Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Test Recorded
                </Label>
                <p className="text-sm text-muted-foreground">
                  {formatDate(testDetails.CreatedDate)}
                </p>
              </div>
            </div>

            {/* Testing Notes */}
            {testDetails.TestingNotes && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  Testing Notes
                </Label>
                <div className="p-3 bg-gray-50 rounded-md border">
                  <p className="text-sm whitespace-pre-wrap">{testDetails.TestingNotes}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">No test details found.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
